import React, { useMemo } from 'react';
import { AddOnCard, AddOnCardProps } from './AddOnCard';
import { AddOnItem, AddOnPrice, AddOnType } from '~/types/models/addon';
import { BillingCycle, Service } from '~/types/models';
import { CUSTOM_DOMAIN_LEARN_MORE } from '~/generated/AnalyticsTypes';
import useAnalytics from '~/react-hooks/use-analytics';
import {
  CustomDomainModalUid,
  hideCustomDomainLearnMoreModal,
} from '~/App/Modules/Settings/Domains/Components/LandingPage/LandingDomainAddOn';
import {
  CustomDomainLearnMoreModal,
  CustomDomainLearnMoreModalProps,
} from './CustomDomainLearnMoreModal';
import CancelCustomDomainAddOnModal, {
  showCancelCustomDomainAddOnModal,
} from './CancelCustomDomainAddOnModal';
import {
  AddOnDescriptions,
  AddOnIcons,
  AddOnLabels,
  getFormattedPrice,
} from '../../../utils';
import { ButtonVariant } from '@vimily/bonjoro-ui-components-v2/Button';
import { useResumeAddOn } from '~/App/Modules/NewBilling/Hooks/useSubscription';

export interface CustomDomainAddOnCardProps {
  service?: Service;
  price?: AddOnPrice;
  details?: AddOnItem;
  billingCycle?: BillingCycle;
}

export function CustomDomainAddOnCard({
  price,
  details,
  service,
  billingCycle,
}: CustomDomainAddOnCardProps) {
  const analytics = useAnalytics();
  const { onResumeClick } = useResumeAddOn(details?.uuid);

  const monthlyPriceItem = price?.addons.find(
    (item) => item.interval === BillingCycle.Monthly && item.is_active
  );
  const yearlyPriceItem = price?.addons.find(
    (item) => item.interval === BillingCycle.Yearly && item.is_active
  );

  const onLearnMoreClick = () => {
    analytics.track(CUSTOM_DOMAIN_LEARN_MORE);
    window.dispatchEvent(new Event(`${CustomDomainModalUid.LearnMore}::open`));
  };

  const onCtaClick = () => {
    window.location.href = '/settings/domains';
  };

  const onCancelClick = (uuid: string) =>
    showCancelCustomDomainAddOnModal({ addOnUuid: uuid });

  const addOnCardProps: AddOnCardProps = useMemo(() => {
    const props: AddOnCardProps = {
      descriptionClassName: 'lg:h-[150px] xl:h-[100px]',
      imgUrl:
        AddOnIcons[AddOnType.CustomDomain][service ?? Service.VideoMessaging],
      title: AddOnLabels[AddOnType.CustomDomain],
      description:
        AddOnDescriptions[AddOnType.CustomDomain][
          service ?? Service.VideoMessaging
        ],
      billingPrice: `$${monthlyPriceItem?.amount as unknown as number}`,
      billingCycle: '/mo',
      billingDescription:
        billingCycle === BillingCycle.Yearly
          ? `Billed $${getFormattedPrice(
              yearlyPriceItem?.amount as unknown as number
            )} USD annually`
          : undefined,
      ctaText: 'Get custom domain',
      onCtaClick,
      onLearnMoreClick,
    };

    if (details?.uuid) {
      return {
        ...props,
        billingPrice: 'Added',
        billingCycle: undefined,
        onCancelClick: () =>
          showCancelCustomDomainAddOnModal({ addOnUuid: details?.uuid }),
        onCtaClick: undefined,
        cancelled: details?.cancelled,
        uuid: details?.uuid,
        cancelledText: details?.cancelledText,
      };
    }

    return props;
  }, [details, monthlyPriceItem, yearlyPriceItem]);

  const learnMoreModalProps: CustomDomainLearnMoreModalProps = useMemo(() => {
    const props = {
      onCtaClick,
    };

    if (details?.uuid) {
      return {
        ...props,
        onCtaClick: () => {
          hideCustomDomainLearnMoreModal();
          if (details?.cancelled) {
            void onResumeClick();
          } else {
            onCancelClick(details?.uuid);
          }
        },
        ctaText: details?.cancelled ? 'Resume add-on' : 'Cancel add-on',
        ctaVariant: ButtonVariant.Secondary,
      };
    }

    return props;
  }, [details]);

  if (!monthlyPriceItem || !yearlyPriceItem) return <></>;

  return (
    <>
      <AddOnCard {...addOnCardProps} />
      <CustomDomainLearnMoreModal {...learnMoreModalProps} />
      <CancelCustomDomainAddOnModal />
    </>
  );
}
